'use client';

import { ReactNode, useEffect, useState } from 'react';
import { GrowthBookProvider } from '@/components/providers/GrowthBookProvider';
import { useSupabaseAuth } from './SupabaseAuthContext';
import { createBrowserClient } from '@/lib/supabase-clients';
import { PlanType } from '@/lib/config/growthbook-features.config';

interface SmartGrowthBookProviderProps {
  children: ReactNode;
}

/**
 * Smart wrapper for GrowthBookProvider that automatically fetches
 * the user's active domain subscription and passes correct plan data.
 *
 * This ensures GrowthBook features are gated correctly app-wide based on
 * the user's actual subscription, not defaulting to 'free'.
 */
export function SmartGrowthBookProvider({ children }: SmartGrowthBookProviderProps) {
  const { isAuthenticated, profile, isLoading: isAuthLoading } = useSupabaseAuth();
  const [subscriptionData, setSubscriptionData] = useState<{
    planType: PlanType;
    seats: number;
    seatsUsed: number;
    credits: number;
    isOwner: boolean;
    userRole: 'owner' | 'admin' | 'contributor' | 'byline' | 'viewer';
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasQueryError, setHasQueryError] = useState(false);

  // Track if profile is still loading/pending (auth in progress or profile not yet fetched)
  const isProfilePending = isAuthLoading || (isAuthenticated && !profile);

  useEffect(() => {
    async function fetchSubscriptionData() {
      if (!isAuthenticated || !profile) {
        // Not authenticated - use free plan defaults
        setSubscriptionData({
          planType: 'free',
          seats: 1,
          seatsUsed: 1,
          credits: 0,
          isOwner: false,
          userRole: 'viewer',
        });
        setIsLoading(false);
        return;
      }

      try {
        setHasQueryError(false);
        const supabase = createBrowserClient();
        const activeDomainId = profile.active_domain_id || profile.domain_id;

        console.log('[SmartGrowthBookProvider] 🔍 Profile data received:', {
          hasProfile: !!profile,
          profileId: profile.id,
          domain_id: profile.domain_id,
          active_domain_id: profile.active_domain_id,
          usingActiveDomain: !!profile.active_domain_id,
          finalDomainId: activeDomainId,
        });

        if (!activeDomainId) {
          console.warn('[SmartGrowthBookProvider] ❌ No active domain found for user');
          setSubscriptionData({
            planType: 'free',
            seats: 1,
            seatsUsed: 1,
            credits: 0,
            isOwner: false,
            userRole: 'viewer',
          });
          setIsLoading(false);
          return;
        }

        // Fetch subscription data directly (can't use DomainSubscriptionService on client-side)
        const { data: subscription } = await supabase
          .from('domain_subscriptions')
          .select('*')
          .eq('domain_id', activeDomainId)
          .single();

        // Fetch user's role in the domain
        const { data: memberData } = await supabase
          .from('domain_members')
          .select('role')
          .eq('domain_id', activeDomainId)
          .eq('user_id', profile.id)
          .single();

        const userRole = (memberData?.role as any) || 'viewer';
        const isOwner = userRole === 'owner';

        // Fetch domain credits
        const { data: creditsData } = await supabase
          .from('domain_credits')
          .select('balance')
          .eq('domain_id', activeDomainId)
          .single();

        setSubscriptionData({
          planType: (subscription?.plan_type || 'free') as PlanType,
          seats: subscription?.seats_purchased || 1,
          seatsUsed: subscription?.seats_used || 1,
          credits: creditsData?.balance ? parseFloat(creditsData.balance) : 0,
          isOwner,
          userRole,
        });

        const finalPlanType = (subscription?.plan_type || 'free') as PlanType;

        console.log('[SmartGrowthBookProvider] ✅ Loaded subscription data:', {
          domainId: activeDomainId,
          planType: finalPlanType,
          seats: subscription?.seats_purchased || 1,
          credits: creditsData?.balance ? parseFloat(creditsData.balance) : 0,
          userRole,
          isOwner,
          stripeSubscriptionId: subscription?.stripe_subscription_id,
          subscriptionStatus: subscription?.status,
          rawSubscription: subscription,
        });

        console.log('[SmartGrowthBookProvider] 🎯 Export should be enabled:', finalPlanType !== 'free');
      } catch (error) {
        console.error('[SmartGrowthBookProvider] Error fetching subscription data:', error);
        // Fall back to free plan on error, but mark as error so blur doesn't show
        setHasQueryError(true);
        setSubscriptionData({
          planType: 'free',
          seats: 1,
          seatsUsed: 1,
          credits: 0,
          isOwner: false,
          userRole: 'viewer',
        });
      } finally {
        setIsLoading(false);
      }
    }

    fetchSubscriptionData();
  }, [isAuthenticated, profile?.id, profile?.active_domain_id, profile?.domain_id]);

  // Show loading state briefly while fetching subscription
  // This prevents flash of incorrect feature gates
  // IMPORTANT: Pass isLoadingSubscription=true so BlurredMomentWrapper doesn't show blur during load
  if (isLoading) {
    return (
      <GrowthBookProvider
        userId={profile?.id || 'anonymous'}
        domainId={profile?.active_domain_id || profile?.domain_id || 'default'}
        planType="free"
        seats={1}
        seatsUsed={1}
        credits={0}
        isOwner={false}
        userRole="viewer"
        isLoadingSubscription={true}
      >
        {children}
      </GrowthBookProvider>
    );
  }

  // Render with actual subscription data
  // IMPORTANT: If profile is still pending (auth loading or profile fetch timed out),
  // OR if there was a query error (RLS failure, etc.),
  // pass isLoadingSubscription=true to prevent blur from showing on Pro accounts
  return (
    <GrowthBookProvider
      userId={profile?.id || 'anonymous'}
      domainId={profile?.active_domain_id || profile?.domain_id || 'default'}
      planType={subscriptionData?.planType || 'free'}
      seats={subscriptionData?.seats || 1}
      seatsUsed={subscriptionData?.seatsUsed || 1}
      credits={subscriptionData?.credits || 0}
      isOwner={subscriptionData?.isOwner || false}
      userRole={subscriptionData?.userRole || 'viewer'}
      isLoadingSubscription={isProfilePending || hasQueryError}
    >
      {children}
    </GrowthBookProvider>
  );
}
